import { Component, inject, signal, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { MessageModule } from 'primeng/message';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { MerchantSummary, UpsertMerchant } from '../../core/models';

// Merchant list — the businesses. A merchant owns locations (the onboarded stores under /locations) and
// the location users that sign in to them. Each row opens the merchant's own page.
@Component({
    selector: 'app-merchant-list',
    standalone: true,
    imports: [FormsModule, CardModule, ButtonModule, TableModule, TagModule, MessageModule, DialogModule, InputTextModule],
    template: `
        <p-card>
            <ng-template pTemplate="title">
                <div class="flex flex-wrap items-center justify-between gap-2">
                    <span>Merchants</span>
                    <p-button label="New merchant" icon="pi pi-plus" size="small" (onClick)="openCreate()" />
                </div>
            </ng-template>

            <p-table [value]="merchants()" [loading]="loading()" [paginator]="merchants().length > 15" [rows]="15">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Locations</th>
                        <th>Users</th>
                        <th>Status</th>
                        <th></th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-m>
                    <tr class="cursor-pointer" (click)="open(m)">
                        <td class="font-medium">{{ m.name }}</td>
                        <td>{{ m.email ?? '—' }}</td>
                        <td>{{ m.phone ?? '—' }}</td>
                        <td>{{ m.locationCount }}</td>
                        <td>{{ m.userCount }}</td>
                        <td><p-tag [value]="m.isActive ? 'Active' : 'Inactive'" [severity]="m.isActive ? 'success' : 'secondary'" /></td>
                        <td><p-button icon="pi pi-arrow-right" [text]="true" (onClick)="open(m)" /></td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="7">{{ error() ?? 'No merchants yet — create one, then add its locations.' }}</td>
                    </tr>
                </ng-template>
            </p-table>
        </p-card>

        <p-dialog header="New merchant" [(visible)]="createOpen" [modal]="true" [style]="{ width: '32rem' }" [breakpoints]="{ '640px': '95vw' }">
            <div class="flex flex-col gap-3">
                <div class="flex flex-col gap-1">
                    <label class="font-medium">Name</label>
                    <input pInputText [(ngModel)]="form.name" placeholder="e.g. Buckeye Beverage Co." />
                </div>
                <div class="flex flex-col gap-1">
                    <label class="font-medium">Email <span class="text-muted-color">(optional)</span></label>
                    <input pInputText type="email" [(ngModel)]="form.email" />
                </div>
                <div class="flex flex-col gap-1">
                    <label class="font-medium">Phone <span class="text-muted-color">(optional)</span></label>
                    <input pInputText [(ngModel)]="form.phone" />
                </div>
                <div class="flex flex-col gap-1">
                    <label class="font-medium">Address <span class="text-muted-color">(optional)</span></label>
                    <input pInputText [(ngModel)]="form.address" />
                </div>
                @if (formError()) { <p-message severity="error">{{ formError() }}</p-message> }
            </div>
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" [disabled]="saving()" (onClick)="createOpen.set(false)" />
                <p-button label="Create merchant" icon="pi pi-check" [loading]="saving()" (onClick)="create()" />
            </ng-template>
        </p-dialog>
    `
})
export class MerchantList implements OnInit {
    private readonly api = inject(MiddlewareApi);
    private readonly router = inject(Router);

    readonly merchants = signal<MerchantSummary[]>([]);
    readonly loading = signal(true);
    readonly error = signal<string | null>(null);

    readonly createOpen = signal(false);
    readonly saving = signal(false);
    readonly formError = signal<string | null>(null);
    form = { name: '', email: '', phone: '', address: '' };

    ngOnInit(): void {
        this.api.getMerchants().subscribe({
            next: (m) => { this.merchants.set(m ?? []); this.loading.set(false); },
            error: (e) => { this.error.set(this.describe(e, 'merchants:read')); this.loading.set(false); }
        });
    }

    open(m: MerchantSummary): void {
        this.router.navigate(['/merchants', m.merchantId]);
    }

    openCreate(): void {
        this.form = { name: '', email: '', phone: '', address: '' };
        this.formError.set(null);
        this.createOpen.set(true);
    }

    create(): void {
        const name = this.form.name.trim();
        if (!name) { this.formError.set('Name is required.'); return; }
        const body: UpsertMerchant = {
            name,
            email: this.form.email.trim() || null,
            phone: this.form.phone.trim() || null,
            address: this.form.address.trim() || null
        };
        this.saving.set(true);
        this.formError.set(null);
        // Straight onto the new merchant's page: adding its locations is the next thing to do.
        this.api.createMerchant(body).subscribe({
            next: (m) => { this.saving.set(false); this.createOpen.set(false); this.open(m); },
            error: (e) => { this.saving.set(false); this.formError.set(this.describe(e, 'merchants:manage')); }
        });
    }

    private describe(e: any, permission: string): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        // 400/404/409 carry { error }: a sentence written for the operator.
        if (e?.error?.error) return e.error.error;
        if (e?.status === 401) return 'Unauthorized — signing in…';
        if (e?.status === 403) return `Forbidden — your role lacks the ${permission} permission.`;
        return `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
